<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import firebase from "@/firebase"
import VueMultiselect from 'vue-multiselect'
import { useStore } from 'vuex'
import acLoader from '../../../components/reusable/acLoader.vue'

const store = useStore()

const currentStep = ref(1)
const newProject = reactive({
    name: '',
    members: [],
    infoBase: "",
    infoBaseType: "folder", // "folder" or "file"
    isClosed: false,
    createdAt: new Date(),
})

// Added state for Google Drive integration
const isLoadingDrive = ref(true)
const driveInitialized = ref(false)
const folders = ref([])
const files = ref([])
const currentFolderId = ref('root')
const folderBreadcrumbs = ref([{ id: 'root', name: 'My Drive' }])

// Get the composable result
const isLoadingUsers = ref(false)
const users = ref([])

const emits = defineEmits(['cancelled', 'submitted'])

const emitCancelled = () => {
    emits('cancelled')
}

const emitSubmitted = () => {
    // Create a copy of the project with only member IDs
    const projectToSubmit = {
        ...newProject,
        members: newProject.members.map(member => { return {
          id: member.id,
          name: member.fullName
        }})
    }
    emits('submitted', projectToSubmit)
}

const getusers = async () => {
    isLoadingUsers.value = true
    const db = firebase.firestore().collection("team")
    const snapshot = await db.get()
    
    // Create a new array instead of pushing to the existing one
    const newUsers = []
    snapshot.docs.forEach((doc) => {
        console.log(doc.id)
        const firstName = doc.data().name.first
        const lastName = doc.data().name.last
        newUsers.push({
            id: doc.id,
            firstName: firstName,
            lastName: lastName,
            fullName: `${firstName} ${lastName}`
        })
    })
    
    // Replace the entire array at once for better reactivity
    users.value = newUsers
    isLoadingUsers.value = false
}

// Load GAPI Script
function loadGapiScript() {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(null);
    script.onerror = () => reject(new Error('Failed to load Google API client'));
    document.head.appendChild(script);
  });
}

// Function to initialize Drive and check for existing permissions
async function initializeDrive() {
  isLoadingDrive.value = true;
  
  try {
    // Get access token from Firebase
    const accessToken = await store.dispatch('user/requestGoogleDriveAccess');
    
    // Initialize Drive API with token
    await initGapiClient(accessToken);
    
    // Load initial folders/files based on selection
    if (newProject.infoBaseType === 'folder') {
      await loadFolders('root');
    } else {
      await loadFiles('root');
    }
    
    driveInitialized.value = true;
  } catch (error) {
    console.error('Failed to initialize Google Drive:', error);
    // Show user-friendly error message
    if (error.message !== 'User cancelled') {
      alert('Google Drive bağlantısı kurulamadı. Lütfen tekrar deneyin.');
    }
  } finally {
    isLoadingDrive.value = false;
  }
}

// Initialize Drive API client with token
async function initGapiClient(accessToken) {
  try {
    // Load GAPI if not already loaded
    if (!window.gapi) {
      await loadGapiScript();
    }
    
    return new Promise((resolve, reject) => {
      window.gapi.load('client', async () => {
        try {
          // Initialize client with proper OAuth scopes
          await window.gapi.client.init({
            clientId: '125219650343-l311uaub3gc76nhbumo65cj0eav3ccr2.apps.googleusercontent.com',
            discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest']
          });
          
          // Set the access token
          window.gapi.client.setToken({ access_token: accessToken });
          
          resolve(window.gapi.client.drive);
        } catch (error) {
          console.error('Error initializing GAPI client:', error);
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error('Failed to initialize GAPI client:', error);
    throw error;
  }
}

// Function to load folders from a specific parent folder
async function loadFolders(folderId) {
  isLoadingDrive.value = true;
  currentFolderId.value = folderId;
  
  try {
    // Make sure we have a valid access token
    if (!window.gapi.client.getToken()) {
      // If token is missing, reinitialize
      const accessToken = await store.dispatch('user/requestGoogleDriveAccess');
      window.gapi.client.setToken({ access_token: accessToken });
    }

    const response = await window.gapi.client.drive.files.list({
      q: `mimeType='application/vnd.google-apps.folder' and '${folderId}' in parents and trashed=false`,
      supportsAllDrives: true,
      spaces: 'drive',
      fields: 'files(id, name)',
    });
    
    folders.value = response.result.files;
    
    // Update breadcrumbs if not root
    if (folderId !== 'root') {
      // Get folder details for breadcrumb
      const folderResponse = await window.gapi.client.drive.files.get({
        fileId: folderId,
        readOnly: true,
        q: 'id, name',
        fields: 'id, name'
      });
      
      // Check if this folder is already in breadcrumbs (going back)
      const existingIndex = folderBreadcrumbs.value.findIndex(crumb => crumb.id === folderId);
      if (existingIndex >= 0) {
        // Truncate breadcrumbs up to this point
        folderBreadcrumbs.value = folderBreadcrumbs.value.slice(0, existingIndex + 1);
      } else {
        // Add to breadcrumbs
        folderBreadcrumbs.value.push({
          id: folderResponse.result.id,
          name: folderResponse.result.name
        });
      }
    } else {
      // Reset breadcrumbs for root
      folderBreadcrumbs.value = [{ id: 'root', name: 'My Drive' }];
    }
  } catch (error) {
    console.error('Error loading folders:', error);
    // If we get an authentication error, try to reinitialize
    if (error.status === 401) {
      try {
        const accessToken = await store.dispatch('user/requestGoogleDriveAccess');
        window.gapi.client.setToken({ access_token: accessToken });
        // Retry the operation
        return loadFolders(folderId);
      } catch (retryError) {
        console.error('Failed to reauthenticate:', retryError);
      }
    }
  } finally {
    isLoadingDrive.value = false;
  }
}

// Function to load files from a specific parent folder
async function loadFiles(folderId) {
  isLoadingDrive.value = true;
  currentFolderId.value = folderId;
  try {
    // Make sure we have a valid access token
    if (!window.gapi.client.getToken()) {
      // If token is missing, reinitialize
      const accessToken = await store.dispatch('user/requestGoogleDriveAccess');
      window.gapi.client.setToken({ access_token: accessToken });
    }
    // Fetch folders
    const foldersResponse = await window.gapi.client.drive.files.list({
      q: `mimeType='application/vnd.google-apps.folder' and '${folderId}' in parents and trashed=false`,
      supportsAllDrives: true,
      spaces: 'drive',
      fields: 'files(id, name)',
    });
    folders.value = foldersResponse.result.files;
    // Fetch files
    const filesResponse = await window.gapi.client.drive.files.list({
      q: `'${folderId}' in parents and trashed=false and mimeType!='application/vnd.google-apps.folder' and (mimeType='application/pdf' or mimeType='application/vnd.openxmlformats-officedocument.wordprocessingml.document' or mimeType='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' or mimeType='application/vnd.google-apps.document' or mimeType='application/vnd.google-apps.spreadsheet')`,
      supportsAllDrives: true,
      spaces: 'drive',
      fields: 'files(id, name, mimeType)',
    });
    files.value = filesResponse.result.files;
    // Update breadcrumbs if not root
    if (folderId !== 'root') {
      const folderResponse = await window.gapi.client.drive.files.get({
        fileId: folderId,
        readOnly: true,
        q: 'id, name',
        fields: 'id, name'
      });
      const existingIndex = folderBreadcrumbs.value.findIndex(crumb => crumb.id === folderId);
      if (existingIndex >= 0) {
        folderBreadcrumbs.value = folderBreadcrumbs.value.slice(0, existingIndex + 1);
      } else {
        folderBreadcrumbs.value.push({
          id: folderResponse.result.id,
          name: folderResponse.result.name
        });
      }
    } else {
      folderBreadcrumbs.value = [{ id: 'root', name: 'My Drive' }];
    }
  } catch (error) {
    console.error('Error loading files:', error);
    if (error.status === 401) {
      try {
        const accessToken = await store.dispatch('user/requestGoogleDriveAccess');
        window.gapi.client.setToken({ access_token: accessToken });
        return loadFiles(folderId);
      } catch (retryError) {
        console.error('Failed to reauthenticate:', retryError);
      }
    }
  } finally {
    isLoadingDrive.value = false;
  }
}

// Function to select a folder as the info base
function selectFolder(folder) {
  newProject.infoBase = folder.id;
}

// Function to select a file as the info base
function selectFile(file) {
  newProject.infoBase = file.id;
}

// Function to go to next step
function nextStep() {
  if (currentStep.value === 1) {
    currentStep.value = 2;
  } else if (currentStep.value === 2) {
    currentStep.value = 3;
    // Initialize Drive when moving to step 3
    initializeDrive();
  }
}

// Function to go back
function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
}

// Function to go to parent folder
function goToParentFolder() {
  if (folderBreadcrumbs.value.length > 1) {
    const parentId = folderBreadcrumbs.value[folderBreadcrumbs.value.length - 2].id;
    if (newProject.infoBaseType === 'folder') {
      loadFolders(parentId);
    } else {
      loadFiles(parentId);
    }
  }
}

onMounted(async () => {
  await getusers();
  console.log(users.value);
});
</script>
<template>
    <div class="step-form-container">
        <form class="ac-main-form !border-none !shadow-none" @submit.prevent="emitSubmitted">
            <div v-if="currentStep === 1" class="step-1-container">
                <div class="notice mb-8 text-2xl text-gray-500">
                    İlk aşamada, projenizin adını ve projeye başlangıçta katılacak kişileri belirleyin. Kişileri daha sonra da ekleyip çıkartabileceksiniz. Projeye kendinizi eklemenize gerek yok, projeyi oluşturan kişi olduğunuz için otomatik olarak ekleneceksiniz.
                </div>
                <!-- Existing step 1 content -->
                <div class="ac-form-control">
                    <label for="name">Proje Adı</label>
                    <input required type="text" v-model="newProject.name" />
                </div>
                <div class="ac-form-control">
                    <label for="users">Üyeler</label>
                    <vue-multiselect v-if="!isLoadingUsers"
                        class="w-full border"
                        v-model="newProject.members"
                        search
                        historyButton
                        historyButtonText="Geri Al"
                        searchPlaceholder="Arama"
                        btnClass="multi-select__button"
                        :multiple="true"
                        :options="users"
                        track-by="id"
                        label="fullName"
                        placeholder="Lütfen eklemek istediğiniz kişileri seçin"
                        selectLabel="Seçmek için tıklayın"
                        selectedLabel="Seçildi"
                        deselectLabel="Kaldır"
                    />
                </div>
            </div>
            
            <div v-if="currentStep === 2" class="step-2-container">

                <div class="ac-form-control">
                    <label class="text-3xl font-bold text-center mb-4">Veri Kaynağı Türü</label>
                    <div class="notice mb-8 text-2xl text-gray-500">
                      Projeniz için hangi türde bir veri kaynağı kullanmak istediğinizi seçin. Tek bir dosya üzerinde çalışabilir veya bütün bir klasörü kullanabilirsiniz.
                    </div>
                    <div class="selection-options grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        <!-- Folder Option -->
                        <div 
                            class="option-card p-6 border-2 rounded-lg cursor-pointer transition-all duration-200"
                            :class="newProject.infoBaseType === 'folder' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'"
                            @click="newProject.infoBaseType = 'folder'"
                        >
                            <div class="flex items-center mb-4">
                                <img src="../../../assets/icons/folder.png" alt="folder" class="w-12 h-12 mr-4">
                                <h3 class="text-xl font-semibold">Klasör</h3>
                            </div>
                            <p class="text-gray-600">
                                Bir klasör seçin ve içindeki tüm dosyaları projenizde kullanın. 
                                Bu seçenek, bütün bir projenin tüm belgelerini kullanmak için idealdir.
                            </p>
                        </div>
                        
                        <!-- File Option -->
                        <div 
                            class="option-card p-6 border-2 rounded-lg cursor-pointer transition-all duration-200"
                            :class="newProject.infoBaseType === 'file' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'"
                            @click="newProject.infoBaseType = 'file'"
                        >
                            <div class="flex items-center mb-4">
                                <img src="../../../assets/icons/file-colored.png" alt="file" class="w-12 h-12 mr-4">
                                <h3 class="text-xl font-semibold">Tek Dosya</h3>
                            </div>
                            <p class="text-gray-600">
                                Belirli bir dosya seçin ve sadece o dosya üzerinde çalışın. 
                                Tek bir dosyayı derinlemesine incelemek için bu seçeneği kullanın.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            
            <div v-if="currentStep === 3" class="step-3-container">
                <div class="ac-form-control">
                    <label class="text-3xl font-bold text-center mb-4" for="infoBase">
                        {{ newProject.infoBaseType === 'folder' ? 'Klasör' : 'Dosya' }} Seçimi
                    </label>
                    
                    <!-- Google Drive folder/file selection -->
                    <div v-if="isLoadingDrive" class="text-center py-4">
                        <div class="border rounded p-3 max-h-60 flex items-center justify-center">
                            <ac-loader  class="top-24" :loaderHeight="200"></ac-loader>
                        </div>
                    </div>
                    
                    <div v-else-if="!driveInitialized" class="text-center py-4">
                        <button 
                            type="button" 
                            class="ac-primary-button" 
                            @click="initializeDrive"
                        >
                            Google Drive'a Bağlan
                        </button>
                    </div>
                    
                    <div v-else class="selection-container">
                        <!-- Breadcrumbs -->
                        <div class="flex items-center justify-between mb-6">
                            <div class="breadcrumbs flex items-center gap-1">
                                <span 
                                    v-for="(crumb, index) in folderBreadcrumbs" 
                                    :key="crumb.id"
                                    class="cursor-pointer hover:underline"
                                    @click="newProject.infoBaseType === 'folder' ? loadFolders(crumb.id) : loadFiles(crumb.id)"
                                >
                                    {{ crumb.name }}
                                    <span v-if="index < folderBreadcrumbs.length - 1" class="mx-1">/</span>
                                </span>
                            </div>
                            
                            <!-- Return to parent folder button -->
                            <button 
                                v-if="folderBreadcrumbs.length > 1"
                                type="button"
                                class="ac-secondary-button text-lg px-4 py-2"
                                @click="goToParentFolder"
                            >
                                ← Üst Klasöre Dön
                            </button>
                        </div>
                        
                        <!-- Folder/File list -->
                        <div class="item-list border rounded p-3 max-h-60 overflow-y-auto">
                            <div v-if="newProject.infoBaseType === 'folder'">
                                <div v-if="folders.length === 0" class="text-center py-2 text-gray-500">
                                    Bu klasörde herhangi bir alt klasör bulunmuyor.
                                </div>
                                <div v-else>
                                    <div 
                                        v-for="folder in folders" 
                                        :key="folder.id"
                                        @dblclick="loadFolders(folder.id)"
                                        class="item flex text-3xl py-4 items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                                    >
                                        <!-- Folder icon -->
                                        <img src="../../../assets/icons/folder.png" alt="folder" class="w-10 h-10 mr-8">
                                        <!-- Folder name with double-click to navigate -->
                                        <span class="flex-grow" @dblclick="loadFolders(folder.id)">{{ folder.name }}</span>
                                        <!-- Select button -->
                                        <button 
                                            type="button" 
                                            class="text-2xl hover:text-green-700"
                                            @click="selectFolder(folder)"
                                        >
                                            Seç
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="newProject.infoBaseType === 'file'">
                                <div v-if="folders.length === 0 && files.length === 0" class="text-center py-2 text-gray-500">
                                    Bu klasörde uygun bir dosya veya alt klasör bulunmuyor.
                                </div>
                                <div v-else>
                                    <!-- Folders for navigation -->
                                    <div 
                                        v-for="folder in folders" 
                                        :key="'folder-' + folder.id"
                                        @dblclick="loadFiles(folder.id)"
                                        class="item flex text-3xl py-4 items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                                    >
                                        <img src="../../../assets/icons/folder.png" alt="folder" class="w-10 h-10 mr-8">
                                        <span class="flex-grow" @dblclick="loadFiles(folder.id)">{{ folder.name }}</span>
                                        <span class="text-gray-400 text-lg">(Klasör)</span>
                                    </div>
                                    <!-- Files for selection -->
                                    <div 
                                        v-for="file in files" 
                                        :key="'file-' + file.id"
                                        class="item flex text-3xl py-4 items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                                    >
                                        <img 
                                            src="../../../assets/icons/file-colored.png" 
                                            :alt="file.name" 
                                            class="w-10 h-10 mr-8"
                                        >
                                        <span class="flex-grow">{{ file.name }}</span>
                                        <button 
                                            type="button" 
                                            class="text-2xl hover:text-green-700"
                                            @click="selectFile(file)"
                                        >
                                            Seç
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                        <p class="text-gray-500 text-lg mt-4">
                              AI sistemimiz şu anda yalnızca PDF, Word, Excel, Google Docs ve Google Sheets dosyalarını desteklemektedir. Sadece bu dosyaları göreceksiniz.
                        </p>
                        <!-- Selected item display -->
                        <div v-if="newProject.infoBase" class="selected-item mt-3 p-3 bg-green-50 border border-green-200 rounded">
                            <div class="font-medium">Seçilen {{ newProject.infoBaseType === 'folder' ? 'Klasör' : 'Dosya' }}:</div>
                            <div v-if="newProject.infoBaseType === 'folder' && folders.find(f => f.id === newProject.infoBase)">
                                {{ folders.find(f => f.id === newProject.infoBase).name }}
                            </div>
                            <div v-else-if="newProject.infoBaseType === 'file' && files.find(f => f.id === newProject.infoBase)">
                                {{ files.find(f => f.id === newProject.infoBase).name }}
                            </div>
                            <div v-else-if="folderBreadcrumbs.length > 0 && folderBreadcrumbs[folderBreadcrumbs.length - 1].id === newProject.infoBase">
                                {{ folderBreadcrumbs[folderBreadcrumbs.length - 1].name }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="button-row flex gap-8 justify-center mt-4">
                <button v-if="currentStep > 1" type="button" class="ac-secondary-button" @click="prevStep">Geri</button>
                <button v-if="currentStep === 1" type="button" class="ac-secondary-button" @click="emitCancelled">İptal</button>
                <button v-if="currentStep === 1" type="button" class="ac-primary-button" @click="nextStep">Devam Et</button>
                <button v-if="currentStep === 2" type="button" class="ac-primary-button" @click="nextStep">Seçim Yap</button>
                <button v-if="currentStep === 3" type="submit" class="ac-primary-button" :disabled="!newProject.infoBase">Oluştur</button>
            </div>
        </form>
    </div>
</template>